import React, {ReactNode} from 'react'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {CurrencyDisplayVariant} from '@vio/core/src/price/business/formatPrice'

import {Price} from '../../search/Price'
import {NUMBER_OF_DECIMALS_FOR_PRICE} from '.'
import {TotalPriceSkeleton, TotalPriceTitleSkeleton} from './Skeleton'

interface PriceTotalProps {
  currencyCode: string
  totalPrice: number
  nights: number
  rooms: number
  guests: number
  isPriceLoading?: boolean
  displayNumberOfRooms?: boolean
  currencyDisplayVariant?: CurrencyDisplayVariant
  hideTaxAndFeesMessage?: boolean
  /** Whether to show the nights, rooms and guests line under the total */
  showStaySummary?: boolean
  /** Shows "Pay at property" in place of the stay summary when `showStaySummary` is false */
  isPayAtProperty?: boolean
  dataId?: string
  /** Replaces the currency figure, e.g. with the price in loyalty points */
  renderPrice?: (value: number, currencyCode: string) => ReactNode
}

const StyledPrice = styled(Price)`
  justify-content: flex-end;
`

const CaptionText = styled(Text)`
  flex: 1;
`

const StaySummary = ({
  nights,
  rooms,
  guests,
  displayNumberOfRooms
}: Pick<
  PriceTotalProps,
  'nights' | 'rooms' | 'guests' | 'displayNumberOfRooms'
>) => (
  <Text variant="bodyS" colorPath="content.neutral.secondary">
    {displayNumberOfRooms ? (
      <FormattedMessageWrapper
        id="roomsAndNightsAndGuests"
        defaultMessage="{nights, plural, one {1 night} other {# nights}}, {rooms, plural, one {1 room} other {# rooms}}, {guests, plural, one {1 guest} other {# guests}}"
        values={{nights, rooms, guests}}
      />
    ) : (
      <FormattedMessageWrapper
        id="nightsAndGuests"
        defaultMessage="{nights, plural, one {1 night} other {# nights}}, {guests, plural, one {1 guest} other {# guests}}"
        values={{nights, guests}}
      />
    )}
  </Text>
)

export const PriceTotal = ({
  currencyCode,
  totalPrice,
  nights,
  rooms,
  guests,
  isPriceLoading = false,
  displayNumberOfRooms = true,
  currencyDisplayVariant,
  hideTaxAndFeesMessage = false,
  showStaySummary = true,
  isPayAtProperty = false,
  dataId,
  renderPrice
}: PriceTotalProps) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      flexWrap="nowrap"
      flexDirection="column"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {isPriceLoading ? (
          <TotalPriceTitleSkeleton />
        ) : (
          <Text variant="titleM">
            <FormattedMessageWrapper
              id="payment.total"
              defaultMessage="Total"
            />
          </Text>
        )}
        {isPriceLoading ? (
          <TotalPriceSkeleton />
        ) : renderPrice ? (
          renderPrice(totalPrice, currencyCode)
        ) : (
          <StyledPrice
            maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
            minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
            value={totalPrice}
            currency={currencyCode}
            size="lg"
            currencyDisplayVariant={currencyDisplayVariant}
            dataId={dataId}
          />
        )}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="s500"
      >
        {showStaySummary ? (
          <StaySummary
            nights={nights}
            rooms={rooms}
            guests={guests}
            displayNumberOfRooms={displayNumberOfRooms}
          />
        ) : isPayAtProperty ? (
          <CaptionText variant="bodyS" colorPath="content.neutral.secondary">
            <FormattedMessageWrapper
              id="paymentSchedule.payAtProperty"
              defaultMessage="Pay at property"
            />
          </CaptionText>
        ) : null}
        {!hideTaxAndFeesMessage ? (
          <CaptionText
            variant="bodyS"
            colorPath="content.neutral.secondary"
            align="right"
          >
            <FormattedMessageWrapper
              id="taxation.withTaxesAndFees"
              defaultMessage="with taxes & fees"
            />
          </CaptionText>
        ) : null}
      </Box>
    </Box>
  )
}
